import { CircularProgress, Grid, Tooltip } from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import React, { useEffect } from "react";

const useStyles = makeStyles(() => ({
  dataCellWrapper: {
    width: "100%",
    height: "100%",
    padding: "2px",
  },
  dataCell: {
    width: "100%",
    height: "100%",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "default",
    "&:hover": {
      border: "2px solid #3c78aa",
    },
  },
  dataCellText: {},
  xLabel: {},
  xLabelWrapper: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
  },
  xLabelsWrapper: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dataCellsWrapper: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
  },
  contentRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
  },
  mainWrapper: {
    width: "100%",
    fontSize: "1rem",
    color: "#666666",
  },
  yLabelCell: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "right",
    paddingRight: "5px",
  },
  tooltip: {},
  tooltipContent: {
    display: "flex",
  },
  tooltipValue: {
    fontWeight: 600,
  },
  tooltipContentWrapper: {
    borderBottom: "1px solid #CCCCCC",
    paddingBottom: "2px",
    paddingTop: "2px",
  },
}));

interface Props {
  data: { value: number; refValue: number }[][];
  columnLabels: string[];
  rowLabels: string[];
  cellHeight: number;
  rowLabelsWidth: number;
  cellColor: (value: number) => string;
  tooltip?: {
    columnGroupLabel: string;
    rowGroupLabel: string;
    valueLabel: string;
    refValueLabel: string;
  };
  cellValueFormatter?: (value: number) => string;
}

interface DataRow {
  rowLabel: string;
  rowData: { value: number; refValue: number }[];
  rowIndex: number;
}

const HeatMap: React.FC<Props> = ({
  data,
  columnLabels,
  rowLabels,
  cellHeight,
  rowLabelsWidth,
  cellColor,
  tooltip,
  cellValueFormatter,
}: Props) => {
  const classes = useStyles();
  const HeaderRow: React.FC = () => {
    return (
      <div className={classes.headerRow}>
        <div style={{ width: rowLabelsWidth }} />
        <div className={classes.xLabelsWrapper}>
          {columnLabels.map((columnLabel, index) => (
            <div key={`xlabel_${index}`} className={classes.xLabelWrapper}>
              <div className={classes.xLabel}>{columnLabel}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const CellTooltip = withStyles(() => ({
    tooltip: {
      backgroundColor: "#f5f5f9",
      color: "#666666",
      fontSize: "0.8rem",
      border: "1px solid #dadde9",
    },
  }))(Tooltip);

  const DataRow: React.FC<DataRow> = ({
    rowLabel,
    rowData,
    rowIndex,
  }: DataRow) => {
    return (
      <div className={classes.contentRow} style={{ height: cellHeight }}>
        <div style={{ width: rowLabelsWidth }} className={classes.yLabelCell}>
          {rowLabel}
        </div>
        <div className={classes.dataCellsWrapper}>
          {rowData.map((cellData, columnIndex) => (
            <div
              key={`yValue_column_${columnIndex}_row_${rowIndex}`}
              className={classes.dataCellWrapper}
            >
              {tooltip !== undefined ? (
                <CellTooltip
                  title={
                    <Grid container>
                      <Grid item xs={12}>
                        <div className={classes.tooltipContentWrapper}>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={6}>
                              {tooltip.rowGroupLabel}
                            </Grid>
                            <Grid item xs={6}>
                              <b>{rowLabel}</b>
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <div className={classes.tooltipContentWrapper}>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={6}>
                              {tooltip.columnGroupLabel}
                            </Grid>
                            <Grid item xs={6}>
                              <b>{columnLabels[columnIndex]}</b>
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <div className={classes.tooltipContentWrapper}>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={6}>
                              {tooltip.valueLabel}
                            </Grid>
                            <Grid item xs={6}>
                              <b>
                                {cellValueFormatter
                                  ? cellValueFormatter(cellData.value)
                                  : cellData.value}
                              </b>
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={6}>
                            {tooltip.refValueLabel}
                          </Grid>
                          <Grid item xs={6}>
                            <b>{cellData.refValue}</b>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  }
                >
                  <div
                    className={classes.dataCell}
                    style={{ background: cellColor(cellData.value) }}
                  >
                    <div className={classes.dataCellText}>
                      {cellValueFormatter
                        ? cellValueFormatter(cellData.value)
                        : cellData.value}
                    </div>
                  </div>
                </CellTooltip>
              ) : (
                <div
                  className={classes.dataCell}
                  style={{ background: cellColor(cellData.value) }}
                >
                  <div className={classes.dataCellText}>
                    {cellValueFormatter
                      ? cellValueFormatter(cellData.value)
                      : cellData.value}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {data && data.length ? (
        <div className={classes.mainWrapper}>
          <HeaderRow />
          {rowLabels.map((rowLabel, rowIndex) => (
            <DataRow
              key={`dataRow_${rowIndex}`}
              rowLabel={rowLabel}
              rowData={data[rowIndex]}
              rowIndex={rowIndex}
            />
          ))}
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </div>
      )}
    </>
  );
};

export default HeatMap;



// WEBPACK FOOTER //
// ./src/components/generic/HeatMap.tsx